<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { resolveCatalogCardComponent } from './cards/registry';
import { useCartStore } from '@entities/order';
import { useModalStore, modalStateEnum } from '@shared/model';
import { useAppearance, useInstitutionStore } from '@entities/institution';
import { useUserProfileStore } from '@entities/user';
import { useAuthStore } from '~/features/auth';
import { setProductOpenSource, useAnalytics, categoryPath, dishPath } from '@shared/lib';
import { cardMinWidthPx } from '@shared/config';

const props = defineProps({
  items: {
    type: Object,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  id: {
    type: String,
    required: true,
  },
  /**
   * Секция в первом экране (первая категория каталога). Её первые карточки
   * грузят фото сразу и с высоким приоритетом — одна из них и станет LCP.
   * Для остальных секций всё остаётся ленивым.
   */
  eagerMedia: {
    type: Boolean,
    default: false,
  },
  /**
   * Тег заголовка секции. На главной категорий много, и каждая — h2; на
   * странице раздела (/menu/<slug>) категория одна, и она же тема страницы,
   * то есть h1. Отдельного заголовка над секцией там быть не должно: два
   * одинаковых заголовка подряд — не структура, а дубль.
   */
  titleTag: {
    type: String,
    default: 'h2',
  },
  /**
   * Slug категории (поле `to`). Пусто — заголовок остаётся текстом: у категории
   * без slug своей страницы нет, и ссылка вела бы в никуда.
   */
  slug: {
    type: String,
    default: '',
  },
});

/** Сколько карточек секции считаем «первым экраном». */
const PRIORITY_CARDS = 4;

/** Ссылка на раздел меню в заголовке — только когда заголовок не сам этот раздел. */
const categoryHref = computed(() =>
  props.titleTag === 'h1' ? '' : categoryPath(props.slug),
);

/** Адрес карточки блюда: настоящая ссылка под названием товара. */
const hrefOf = (item: any) => dishPath(item);

const storeUserProfile = useUserProfileStore();
const storeAuth = useAuthStore();
const storeModal = useModalStore();
const cartStore = useCartStore();
const storeInstitution = useInstitutionStore();

// Внешний вид каталога: шаблон карточки, колонки, флаги показа.
// Конфиг приходит из настроек заведения (visual.params_json.appearance).
const { catalog: catalogAppearance } = useAppearance();

const cardComponent = computed(() => resolveCatalogCardComponent(catalogAppearance.value.template));
/** «Список» — всегда одна колонка во всю ширину, сколько бы ни выбрали в настройках. */
const isRowLayout = computed(() => catalogAppearance.value.template === 'row');

const gridStyle = computed(() => ({
  // Колонки — потолок, а не жёсткое число: на экранах поуже сетка сама укладывает
  // меньше колонок, как только карточка становится уже --pcard-min (см. .pcard-grid).
  '--pcard-cols-desktop': String(catalogAppearance.value.columnsDesktop),
  '--pcard-cols-mobile': String(catalogAppearance.value.columnsMobile),
  '--pcard-min': `${cardMinWidthPx(catalogAppearance.value.template, catalogAppearance.value.columnsDesktop)}px`,
}));

// Функция для нормализации items в массив
const normalizeItems = (items: any): any[] => {
  if (Array.isArray(items)) {
    return items;
  }
  if (items && typeof items === 'object') {
    return Object.values(items);
  }
  return [];
};

// Используем ref для локального состояния, которое можно изменять (для is_favorite)
const localItems = ref<any[]>(normalizeItems(props.items));

// Отслеживаем изменения props.items и полностью обновляем localItems
watch(
  () => props.items,
  (newItems) => {
    // Полностью заменяем массив для гарантированной реактивности
    localItems.value = [...normalizeItems(newItems)];
  },
  { deep: true, immediate: true },
);

const openMoreAboutDish = (dish: any) => {
  // Источник открытия читают события внутри карточки: без него непонятно, откуда
  // приходят добавления — из каталога, из рекомендаций или из пуша.
  setProductOpenSource('catalog');

  // Клик по карточке — отдельно от открытия модалки: с позицией в выдаче видно,
  // спрос это или просто верхняя строчка каталога.
  useAnalytics().track('product_card_click', {
    productId: Number(dish?.id) || 0,
    categoryId: dish?.category_id ?? undefined,
    price: Number(dish?.price) || undefined,
    position: localItems.value.findIndex((item: any) => item?.id === dish?.id) + 1 || undefined,
    source: 'catalog',
  });

  cartStore.setSelectedDish(dish);
  storeModal.open(modalStateEnum.MoreAboutDishRefactored);
};

// ===== Magnetic hover: наклон карточки под курсором и лёгкое приближение соседей =====
const cardRefs = ref<{ [key: string]: HTMLElement | null }>({});

const setCardRef = (el: any, itemId: string) => {
  if (el) {
    cardRefs.value[itemId] = el;
  }
};

const getDistance = (x1: number, y1: number, x2: number, y2: number) =>
  Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));

/** Соседи под курсором чуть подрастают — эффект затухает к 400px. */
const applyProximityEffect = (card: HTMLElement, distance: number) => {
  const maxDistance = 400;
  if (distance > maxDistance) {
    card.style.setProperty('--proximity-scale', '1');
    return;
  }
  const intensity = 1 - distance / maxDistance;
  card.style.setProperty('--proximity-scale', String(1 + intensity * 0.02));
};

const handleCardMouseMove = (event: MouseEvent, itemId: string) => {
  const card = cardRefs.value[itemId];
  if (!card) return;

  const rect = card.getBoundingClientRect();
  const x = event.clientX - rect.left;
  const y = event.clientY - rect.top;
  const centerX = rect.width / 2;
  const centerY = rect.height / 2;

  const rotateX = (y - centerY) / 30;
  const rotateY = (centerX - x) / 30;

  card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
  card.style.setProperty('--proximity-scale', '1.02');

  Object.entries(cardRefs.value).forEach(([otherItemId, otherCard]) => {
    if (otherItemId === itemId || !otherCard) return;
    const otherRect = otherCard.getBoundingClientRect();
    const distance = getDistance(
      event.clientX,
      event.clientY,
      otherRect.left + otherRect.width / 2,
      otherRect.top + otherRect.height / 2,
    );
    applyProximityEffect(otherCard, distance);
  });
};

const handleCardMouseLeave = (itemId: string) => {
  const card = cardRefs.value[itemId];
  if (!card) return;

  card.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
  Object.values(cardRefs.value).forEach((otherCard) => {
    otherCard?.style.setProperty('--proximity-scale', '1');
  });
};

// Количество каждой позиции корзины по ключу «id товара + филиал».
// Один и тот же товар лежит в корзине несколькими строками, если у них разные
// добавки, — поэтому суммируем, а не перезаписываем.
const cartQuantityMap = computed(() => {
  const map = new Map<string, number>();
  cartStore.cart.forEach((item: any) => {
    const key = `${item.id}_${item.branch_id}`;
    map.set(key, (map.get(key) ?? 0) + (Number(item.quantity) || 0));
  });
  return map;
});

/**
 * Корзину показываем только после гидратации.
 *
 * На сервере корзины нет — SSR всегда рисует «плюс». Если на клиенте состояние
 * отличается уже в момент гидратации (корзина восстановилась из localStorage до неё),
 * Vue такое расхождение НЕ чинит: несовпадение классов и атрибутов оно только логирует,
 * а DOM оставляет серверный. Кнопка так и застревает на «плюсе», хотя товар в корзине.
 *
 * Раньше состояние переключалось через v-if/v-else — это структурное различие, его
 * гидратация исправляет. Теперь это класс и иконка, поэтому первый клиентский рендер
 * обязан совпасть с серверным, а корзина доезжает следующим тиком — обычным патчем.
 */
const hydrated = ref(false);
onMounted(() => {
  hydrated.value = true;
});

/**
 * Сколько штук этого товара уже в корзине.
 *
 * Для группы считаем по всем вариантам: в корзине лежит конкретный вариант
 * («Стандартная»), а в каталоге — карточка группы, и по id группы позиция не найдётся.
 */
const cartQuantityOf = (item: any): number => {
  if (!hydrated.value) return 0;

  const currentBranchId = storeInstitution.selectedBranch?.id;
  if (!currentBranchId) return 0;

  if (item.type === 'combo') {
    return cartStore.cart.reduce(
      (sum: number, cartItem: any) =>
        cartItem.itemType === 'combo' &&
        String(cartItem.comboId) === String(item.id) &&
        String(cartItem.branch_id) === String(currentBranchId)
          ? sum + (Number(cartItem.quantity) || 0)
          : sum,
      0,
    );
  }

  if (item.type === 'group' && Array.isArray(item.variants)) {
    return item.variants.reduce(
      (sum: number, variant: any) => sum + (cartQuantityMap.value.get(`${variant.id}_${currentBranchId}`) ?? 0),
      0,
    );
  }

  return cartQuantityMap.value.get(`${item.id}_${currentBranchId}`) ?? 0;
};

const placeholderImg = computed(() => (storeInstitution.institution as any)?.visual?.placeholder_file);

/** Сердечко показываем только авторизованным и только если оно не выключено в настройках. */
const favoriteEnabled = computed(() => storeAuth.isLoggedIn && catalogAppearance.value.showFavorite);

// ===== Реактивное управление избранным =====
const favoriteAnimating = ref<Record<string, 'add' | 'remove'>>({});

const updateFavoriteStatus = (item: any, isFavorite: boolean) => {
  item.is_favorite = isFavorite;
};

/** API избранного понимает single | group | combo; без type считаем одиночным товаром. */
const resolveFavoriteItemType = (item: any): 'single' | 'group' | 'combo' => {
  const t = item?.type;
  if (t === 'group' || t === 'combo') return t;
  return 'single';
};

/** После fetchProducts props пересобирают localItems — старый `item` из замыкания уже не в списке. */
const applyFavoriteToLocalItems = (productId: number | string, isFavorite: boolean) => {
  const sid = String(productId);
  for (const p of localItems.value) {
    if (String(p?.id) === sid) updateFavoriteStatus(p, isFavorite);
  }
};

const triggerFavoriteAnimation = (itemId: string, type: 'add' | 'remove') => {
  favoriteAnimating.value = { ...favoriteAnimating.value, [itemId]: type };
  setTimeout(() => {
    const next = { ...favoriteAnimating.value };
    delete next[itemId];
    favoriteAnimating.value = next;
  }, 700);
};

const onFavoriteClick = async (item: any) => {
  const userId = storeUserProfile.getUserId;
  const institutionId = storeInstitution.institution?.id;
  if (!storeAuth.isLoggedIn) return;

  const productId = item.id;
  const itemId = String(productId);
  const wasFavorite = !!item.is_favorite;
  const favType = resolveFavoriteItemType(item);

  triggerFavoriteAnimation(itemId, wasFavorite ? 'remove' : 'add');

  try {
    if (wasFavorite) {
      await storeInstitution.removeFromFavorites(productId, Number(userId) || 0, Number(institutionId) || 0, favType);
      await nextTick();
      applyFavoriteToLocalItems(productId, false);
    } else {
      await storeInstitution.addToFavorites(productId, Number(userId) || 0, Number(institutionId) || 0, favType);
      await nextTick();
      applyFavoriteToLocalItems(productId, true);
    }
  } catch {
    await nextTick();
    applyFavoriteToLocalItems(productId, wasFavorite);
  }
};
</script>

<template>
  <section :id="id" class="dish">
    <!-- Заголовок категории — ссылка на её раздел меню. Это единственная
         внутренняя ссылка на /menu/<slug> с главной: якорь #slug никуда не
         ведёт, робот по нему в раздел не попадает. На самой странице раздела
         ссылка не нужна — она вела бы на саму себя. -->
    <component :is="titleTag" class="dish__title text-2xl lg:text-3xl font-semibold">
      <NuxtLink v-if="categoryHref" :to="categoryHref" class="dish__title-link">{{ title }}</NuxtLink>
      <template v-else>{{ title }}</template>
    </component>
    <div class="pcard-grid" :class="{ 'pcard-grid--rows': isRowLayout }" :style="gridStyle">
      <div
        v-for="(item, index) in localItems"
        :key="`${item.id}-${index}`"
        :ref="(el) => setCardRef(el as HTMLElement, String(item.id))"
        class="pcard-slot"
        @mousemove="(e) => handleCardMouseMove(e, String(item.id))"
        @mouseleave="() => handleCardMouseLeave(String(item.id))"
      >
        <component
          :is="cardComponent"
          :item="item"
          :config="catalogAppearance"
          :href="hrefOf(item)"
          :priority="eagerMedia && index < PRIORITY_CARDS"
          :placeholder="placeholderImg"
          :in-cart="cartQuantityOf(item) > 0"
          :cart-quantity="cartQuantityOf(item)"
          :favorite-enabled="favoriteEnabled"
          :favorite-animation="favoriteAnimating[String(item.id)] ?? null"
          @open="openMoreAboutDish(item)"
          @action="openMoreAboutDish(item)"
          @favorite="onFavoriteClick(item)"
        />
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.dish {
  @apply pt-16 md:pt-32;

  &__title {
    // Названия категорий — одно из мест, где включается фирменный шрифт заведения
    font-family: var(--app-brand-font, inherit);
  }

  // Заголовок-ссылка выглядит ровно как заголовок: подчёркивание и синий цвет
  // здесь только сообщили бы гостю о переходе, которого он не просил.
  &__title-link {
    @apply no-underline;
    color: inherit;
  }
}

// Обёртка грид-ячейки: на ней живёт magnetic-эффект, чтобы шаблоны карточек
// оставались чистым представлением и ничего не знали про курсор.
.pcard-slot {
  @apply h-full;
  transform-style: preserve-3d;
  will-change: transform;
  transform: scale(var(--proximity-scale, 1));
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.pcard-slot > :deep(.pcard) {
  @apply h-full;
}

@media (hover: none) and (pointer: coarse) {
  .pcard-slot {
    transform: none !important;
  }
}

@media (prefers-reduced-motion: reduce) {
  .pcard-slot {
    transition: none;
    transform: none !important;
  }
}
</style>
